import React from 'react';
import { withTheme } from 'styled-components';
import FlightSearchForm from '@g4/next-furniture/FlightSearchForm';
import PropTypes from 'prop-types';
import { useSSRQuery, useLazySSRQuery } from '../../graphql';
import SunseekerPromoPopup from './SunseekerPromotion/SunseekerPromoPopup';
import NEARBY_FLIGHT_LOCATIONS_QUERY from './wrappedFlightSearchForm/nearbyFlightLocations.query.graphql';
import FLIGHT_MARKET_QUERY from './wrappedFlightSearchForm/flightMarket.query.graphql';
import OVERLAY_QUERY from './SunseekerPromotion/overlay.query.graphql.js';

const WrappedFlightSearchForm = props => {
  const [fetchNearbyLocations, nearbyLocationsResponse] = useLazySSRQuery(
    NEARBY_FLIGHT_LOCATIONS_QUERY
  );

  const [fetchMarketQuery, marketResponse] = useLazySSRQuery(FLIGHT_MARKET_QUERY);

  const fetchMarket = React.useCallback(
    ({ variables, ...rest } = {}) => {
      const { operatedBy, ...otherVariables } = variables || {};
      return fetchMarketQuery({
        variables: {
          ...otherVariables,
          operatedBy: operatedBy != null
            ? (Array.isArray(operatedBy) ? operatedBy : [operatedBy])
            : operatedBy,
        },
        ...rest,
      });
    },
    [fetchMarketQuery]
  );

  const [fetchMerchandiseOverlay, { data }] = useLazySSRQuery(OVERLAY_QUERY);

  const onDestinationChange = d => {
    fetchMerchandiseOverlay({
      variables: {
        overlaySearchCriteria: {
          destinationTag: d.airport.code,
        },
      },
    });
  };

  React.useEffect(() => {
    const shouldUpdateErrorDisplay = () => {
      const shouldDisplayErrorBanner =
        marketResponse.called &&
        !marketResponse.loading &&
        marketResponse.data.flightMarket.calendar === null;
      props.setIsFlightMarketError(shouldDisplayErrorBanner);
    };
    shouldUpdateErrorDisplay();
  }, [marketResponse, props]);

  const errors = [
    marketResponse.error,
    nearbyLocationsResponse.error,
  ].filter(Boolean);

  if (errors.length) {
    // Give our custom error a human-readable message
    const error = new Error(
      `Failed to query flight search form data: ${errors.join(', ')}`
    );

    // Add each GraphQL error to the new custom error
    error.apiErrors = errors;

    throw error;
  }

  const translations =
    props.initialDataResponse.data.application.translations.flightSearchForm;
  const configurations = props.initialDataResponse.data.application.configurations;
  const locations = props.initialDataResponse.data.flightLocations;
  const sunCountryImage = props.initialDataResponse?.data?.application?.images?.find(
    (image) => image.id === 'sunCountryDropdownImage'
  );

  return (
    <>
      {data?.application?.merchandiseOverlay?.content && (
        <SunseekerPromoPopup
          overlayContent={data.application.merchandiseOverlay.content}
        />
      )}
      <FlightSearchForm
        translations={translations}
        dropDownLogo={sunCountryImage}
        configurations={configurations}
        locations={locations}
        fetchNearbyLocations={fetchNearbyLocations}
        nearbyLocationsLoading={nearbyLocationsResponse.loading}
        nearbyLocations={
          nearbyLocationsResponse.data
            ? nearbyLocationsResponse.data.flightLocations
            : []
        }
        fetchMarket={fetchMarket}
        marketLoading={marketResponse.loading}
        market={marketResponse.data}
        onDestinationChange={onDestinationChange}
        isTrackingEnabled={true}
        {...props}
      />
    </>
  );
};

WrappedFlightSearchForm.propTypes = {
  setIsFlightMarketError: PropTypes.func,
};

export default withTheme(WrappedFlightSearchForm);
